import { z } from 'zod';

import {
  AchievementId,
  CredentialType,
  Email,
  SessionId,
  UserId,
  WebauthnId,
  WebauthnPrfSupportStatus,
} from 'backend-client-web';
import { encodeBase64, Hash, loadHash, randomSeed } from 'vault-crypto';

import { brandedUint8Array } from '../brands';
import { PersistableAuthenticator } from '../clientCoreParameters/authenticator/PersistableAuthenticator';
import { makeProtobufEnumSchema } from '../enumSchemas';
import { RelatedProfile } from '../relatedProfile/RelatedProfile';
import { ServerSideSettings } from '../serverSideSettings/ServerSideSettings';
import { Session } from '../session/Session';
import { WithUnknownValuePlaceholder } from '../unknownValuePlaceholder';
import { UserClientSettings } from '../userClientSettings/UserClientSettings';
import { undefinedFromNullish } from '../util/undefinedFromNullish';

import { PersistableOrganization } from './PersistableOrganization';

interface PersistableWebauthnCredential {
  webauthnId: WebauthnId;
  credentialType: WithUnknownValuePlaceholder<CredentialType>;
  aaguid: string | undefined;
  sessionId: SessionId | undefined;
  createdAt: Date;
  lastUsedAt: Date;
  prfSupportStatus: WithUnknownValuePlaceholder<WebauthnPrfSupportStatus>;
}

const PersistableWebauthnCredential = z.object({
  // this field was added late, so we default to a random id if it was missing. this will fix itself
  // on the next sync update, and operations that work on this value will fail reasonably gracefully
  // until then.
  // TODO remove this fallback when minimum version hits 2023-09-28
  webauthnId: z.string().default(() =>
    // b64 urlsafe encoding
    encodeBase64(randomSeed()).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, ''),
  ),
  credentialType: makeProtobufEnumSchema(CredentialType, CredentialType.UNKNOWN).default(
    CredentialType.UNKNOWN,
  ),
  aaguid: undefinedFromNullish(z.string()),
  sessionId: undefinedFromNullish(z.string()),
  createdAt: z.date(),
  lastUsedAt: z.date(),
  prfSupportStatus: makeProtobufEnumSchema(
    WebauthnPrfSupportStatus,
    WebauthnPrfSupportStatus.UNKNOWN,
  ).default(WebauthnPrfSupportStatus.UNKNOWN),
});

interface PersistableAccountState {
  userId: UserId;
  preferredLocale?: string | undefined;
  serverSideSettings?: ServerSideSettings;
  userClientSettings?: UserClientSettings;
  authenticators: PersistableAuthenticator[];
  sessions: Session[];
  achievements?: Array<AchievementId>;
  localAchievements?: Array<AchievementId>;
  authenticatorBlockHash?: Hash;
  organizations: Array<PersistableOrganization>;
  relatedProfiles?: { [profileId: string]: RelatedProfile };
  webauthnCredentials: Array<PersistableWebauthnCredential>;
  profilesLocked: boolean;

  // deprecated
  email?: Email | null;
  unverifiedEmail?: Email | null;
  fullName?: string | null;
  emails?: {
    email: string;
    primary: boolean;
    verificationPending: boolean;
  }[];
}

const PersistableAccountState = z.object({
  userId: z.uuid(),
  preferredLocale: undefinedFromNullish(z.string()).optional(),
  serverSideSettings: ServerSideSettings.optional(),
  userClientSettings: UserClientSettings.optional(),
  authenticators: z.array(PersistableAuthenticator),
  sessions: z.array(Session),
  achievements: z.number().array().optional(),
  localAchievements: z.number().array().optional(),
  authenticatorBlockHash: brandedUint8Array(loadHash).optional(),
  organizations: z.array(PersistableOrganization).default([]),
  relatedProfiles: z.record(z.string(), RelatedProfile).optional(),
  webauthnCredentials: z.array(PersistableWebauthnCredential).default([]),
  profilesLocked: z.boolean().default(false),

  // deprecated
  email: z.string().nullable().optional(),
  unverifiedEmail: z.string().nullable().optional(),
  fullName: z.string().nullable().optional(),
  emails: z
    .array(
      z.object({
        email: z.string(),
        primary: z.boolean(),
        verificationPending: z.boolean(),
      }),
    )
    .optional(),
});

export { PersistableWebauthnCredential, PersistableAccountState };
